/**
 * ProjectedJobEconomicsPanel — read-only internal economics view for
 * `/installer-offers/:jobId/review`.
 *
 * Phase A: this panel now consumes the canonical read bridge
 * `getJobCostBreakdown()` instead of ad-hoc derivation. Every line carries
 * a lifecycle + reliability tag and missing categories are surfaced
 * explicitly (never silently zeroed).
 */
import { useState, useMemo } from "react";
import type { Job } from "@/data/types";
import {
  getJobCostBreakdown,
  type JobCostLine,
  type CostReliability,
  type CostLifecycle,
} from "@/data/jobCost/jobCostBreakdown";
import { useVendorPO } from "@/data/VendorPOContext";
import { useWarehouseInventory } from "@/data/WarehouseInventoryContext";
import { useJobExpenseLedger } from "@/hooks/useJobExpenseLedger";
import { Badge } from "@/components/ui/badge";
import {
  ChevronDown,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Minus,
  Info,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  job: Job;
  visible: boolean;
  draftPayoutAmount?: { amount: number | null; isDraft: boolean };
  defaultOpen?: boolean;
}

const fmt = (n: number) =>
  `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const pct = (n: number) => `${(n * 100).toFixed(1)}%`;

const RELIABILITY_LABEL: Record<CostReliability, string> = {
  actual: "Actual",
  estimated: "Estimated",
  projected: "Projected",
  allocated: "Allocated",
  missing: "Not yet tracked",
};

const LIFECYCLE_LABEL: Record<CostLifecycle, string> = {
  projected: "Projected",
  committed: "Committed",
  realized: "Realized",
  allocated: "Allocated",
  reserve: "Reserve",
};

function ReliabilityTag({ r }: { r: CostReliability }) {
  const cls =
    r === "actual"
      ? "text-success"
      : r === "missing"
      ? "text-muted-foreground/70 italic"
      : "text-muted-foreground";
  return <span className={cn("text-[10px]", cls)}>{RELIABILITY_LABEL[r]}</span>;
}

function LifecycleTag({ l }: { l: CostLifecycle }) {
  return (
    <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
      {LIFECYCLE_LABEL[l]}
    </span>
  );
}

export function ProjectedJobEconomicsPanel({
  job,
  visible,
  draftPayoutAmount,
  defaultOpen = false,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);

  const draftValue =
    draftPayoutAmount?.isDraft && draftPayoutAmount.amount !== null
      ? draftPayoutAmount.amount
      : null;
  const usingDraft = draftValue !== null;

  const { purchaseOrders } = useVendorPO();
  const { receivingRecords } = useWarehouseInventory();
  const { entries: ledgerEntries, authorized: ledgerAuthorized } =
    useJobExpenseLedger(job.id);

  const breakdown = useMemo(
    () =>
      getJobCostBreakdown(job, {
        draftOfferCompensation: draftValue,
        materialProcurement: { purchaseOrders, receivingRecords },
        expenseLedgerEntries: ledgerEntries,
      }),
    [job, draftValue, purchaseOrders, receivingRecords, ledgerEntries],
  );

  if (!visible) return null;

  const { totals, hasContractValue, direct, allocations } = breakdown;
  const tone =
    totals.projectedNetMargin >= 0.3
      ? "good"
      : totals.projectedNetMargin >= 0.15
      ? "neutral"
      : hasContractValue
      ? "bad"
      : "neutral";
  const MarginIcon = tone === "good" ? TrendingUp : tone === "bad" ? TrendingDown : Minus;
  const marginColor =
    tone === "good"
      ? "text-success"
      : tone === "bad"
      ? "text-destructive"
      : "text-muted-foreground";

  return (
    <div className="rounded-lg border bg-card overflow-hidden" data-testid="projected-economics">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        <span className="text-sm font-semibold">Projected Job Economics</span>
        <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
          Internal Only
        </Badge>
        <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
          Projected
        </Badge>
        {usingDraft && (
          <Badge
            variant="secondary"
            className="text-[10px] uppercase tracking-wider"
            data-testid="using-draft-badge"
          >
            Using Draft Offer
          </Badge>
        )}
        {hasContractValue && (
          <span className={cn("ml-auto flex items-center gap-1 text-sm font-bold", marginColor)}>
            <MarginIcon className="h-3.5 w-3.5" />
            {pct(totals.projectedNetMargin)}
          </span>
        )}
      </button>

      {open && (
        <div className="p-4 space-y-4">
          {/* Coverage summary */}
          <div className="flex flex-wrap gap-1.5 text-[10px]">
            <Badge variant="outline">{totals.realizedCostCount} realized</Badge>
            <Badge variant="outline">{totals.projectedCostCount} projected</Badge>
            <Badge variant="outline" className="text-muted-foreground">
              {totals.missingCostCount} missing
            </Badge>
          </div>

          {/* 1. Revenue */}
          <Section title="Revenue">
            <Row
              label="Contract Value"
              value={hasContractValue ? fmt(breakdown.contractValue) : "—"}
              reliability={hasContractValue ? "actual" : "missing"}
              lifecycle="realized"
              bold
            />
          </Section>

          {/* 2. Direct Job Costs */}
          <Section title="Direct Job Costs">
            {direct.map((l) => (
              <LineRow key={l.category} line={l} />
            ))}
          </Section>

          {/* 3. Operating Allocations */}
          <Section title="Operating Allocations">
            {allocations.map((l) => (
              <LineRow key={l.category} line={l} />
            ))}
          </Section>

          {/* 4. Contribution Summary */}
          <Section title="Contribution Summary">
            <Row
              label="Projected Gross Contribution"
              value={hasContractValue ? fmt(totals.projectedGrossContribution) : "—"}
              reliability="projected"
              lifecycle="projected"
              bold
              color={
                !hasContractValue
                  ? "text-muted-foreground"
                  : totals.projectedGrossContribution >= 0
                  ? "text-success"
                  : "text-destructive"
              }
            />
            <Row
              label="Projected Gross Margin"
              value={hasContractValue ? pct(totals.projectedGrossMargin) : "—"}
              reliability="projected"
              lifecycle="projected"
              bold
            />
            {totals.realizedGrossContribution !== null && (
              <Row
                label="Realized Gross Contribution"
                value={fmt(totals.realizedGrossContribution)}
                reliability="actual"
                lifecycle="realized"
                bold
                color="text-success"
              />
            )}
            <Row
              label="Projected Net Contribution"
              value={hasContractValue ? fmt(totals.projectedNetContribution) : "—"}
              reliability="projected"
              lifecycle="projected"
              bold
              color={
                !hasContractValue
                  ? "text-muted-foreground"
                  : totals.projectedNetContribution >= 0
                  ? "text-success"
                  : "text-destructive"
              }
              sub="Untracked allocations excluded — not a final net"
            />
            <Row
              label="Projected Net Margin"
              value={hasContractValue ? pct(totals.projectedNetMargin) : "—"}
              reliability="projected"
              lifecycle="projected"
              bold
              color={marginColor}
            />
          </Section>

          <div className="flex items-start gap-1.5 rounded-md bg-muted/40 px-2.5 py-2 text-[10px] text-muted-foreground">
            <Info className="h-3 w-3 mt-0.5 shrink-0" />
            <span>
              {breakdown.disclaimer}
              {usingDraft && (
                <> Numbers reflect the draft Offer Compensation in the builder above; save to commit.</>
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {title}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function LineRow({ line }: { line: JobCostLine }) {
  return (
    <Row
      label={line.label}
      value={line.amount !== null ? `-${fmt(line.amount)}` : "—"}
      reliability={line.reliability}
      lifecycle={line.lifecycle}
      muted
      sub={line.notes}
    />
  );
}

function Row({
  label,
  value,
  reliability,
  lifecycle,
  bold,
  muted,
  color,
  sub,
}: {
  label: string;
  value: string;
  reliability: CostReliability;
  lifecycle: CostLifecycle;
  bold?: boolean;
  muted?: boolean;
  color?: string;
  sub?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={cn(
              "text-xs",
              bold
                ? "font-semibold text-foreground"
                : muted
                ? "text-muted-foreground"
                : "text-foreground",
            )}
          >
            {label}
          </span>
          <ReliabilityTag r={reliability} />
          <LifecycleTag l={lifecycle} />
        </div>
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
      </div>
      <span
        className={cn(
          "text-sm font-mono tabular-nums shrink-0",
          bold ? "font-bold" : "font-medium",
          color ?? (muted ? "text-muted-foreground" : "text-foreground"),
        )}
      >
        {value}
      </span>
    </div>
  );
}
